import { createFileRoute, Link } from "@tanstack/react-router";
import { HeartHandshake, Check, X, AlertTriangle } from "lucide-react";
import { ClinicalDisclaimer } from "@/components/BeforeYouBegin";

export const Route = createFileRoute("/community-guidelines")({
  head: () => ({
    meta: [
      { title: "Community Guidelines — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Our shared agreements for a safe, trauma-informed peer support community.",
      },
      { property: "og:title", content: "Community Guidelines — Recovery Trauma™" },
      { property: "og:description", content: "Our shared agreements for a safe, trauma-informed peer support community." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/community-guidelines" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/community-guidelines" }],
  }),
  component: GuidelinesPage,
});

const ENCOURAGE = [
  "Sharing your own experiences",
  "Offering support and witnessing others",
  "Respectful, compassionate discussion",
  "Honoring different healing paths",
];

const PROHIBIT = [
  "Graphic trauma details (please use content notes)",
  "Giving medical, clinical, or therapeutic advice",
  "Diagnosing other members",
  "Harassment, shaming, or unsolicited criticism",
  "Crisis intervention requests — please use emergency resources",
];

function GuidelinesPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <header className="mb-8 text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <HeartHandshake className="h-3.5 w-3.5" /> Community Safety
        </div>
        <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">
          Our Shared Agreements
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">
          Recovery Trauma™ is a peer-support community —{" "}
          <strong className="font-semibold text-foreground">
            not a crisis service.
          </strong>{" "}
          These agreements help keep our space safe for everyone.
        </p>
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <h2 className="font-serif text-xl text-foreground">We encourage</h2>
          <ul className="mt-4 space-y-3">
            {ENCOURAGE.map((t) => (
              <li key={t} className="flex items-start gap-2.5 text-sm text-foreground/85">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--sage-deep,#7d9b76)]" />
                {t}
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <h2 className="font-serif text-xl text-foreground">We do not allow</h2>
          <ul className="mt-4 space-y-3">
            {PROHIBIT.map((t) => (
              <li key={t} className="flex items-start gap-2.5 text-sm text-foreground/85">
                <X className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                {t}
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-card p-5 text-sm leading-relaxed text-foreground/85">
        <p>
          <strong>Community safety notice:</strong> Information shared by
          members reflects personal experiences and opinions only and should
          not be considered medical, psychological, legal, or professional
          advice.
        </p>
      </div>

      <div className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-300/40 bg-amber-50/50 p-5">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
        <p className="text-sm leading-relaxed text-foreground">
          <strong className="font-semibold">In crisis?</strong> Recovery Trauma™
          is not a crisis service. Please reach out to a qualified provider or{" "}
          <Link to="/crisis" className="font-semibold underline">
            view crisis resources
          </Link>
          .
        </p>
      </div>

      <div className="mt-6">
        <ClinicalDisclaimer />
      </div>
    </div>
  );
}
